import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import SendIcon from '@mui/icons-material/SendRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import ImageIcon from '@mui/icons-material/ImageRounded';
import ShareIcon from '@mui/icons-material/ShareRounded';
import NotesIcon from '@mui/icons-material/NotesRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import AttachFileIcon from '@mui/icons-material/AttachFileRounded';
import VideocamIcon from '@mui/icons-material/VideocamRounded';
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded';
import EventIcon from '@mui/icons-material/EventRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineRounded';
import HistoryIcon from '@mui/icons-material/HistoryRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

import { AuthContext } from "../../contexts/auth";
import api from "../../services/api";
import PricingModal from "../pricingModal";
import VideoCreditsModal from "../videoCreditsModal";

// ─── Copy Button ─────────────────────────────────────────────────────────────

const CopyBtn = ({ text }) => {
    const { t } = useTranslation();
    const [ok, setOk] = useState(false);
    const copy = () => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 2000); };
    return (
        <Tooltip title={ok ? t('agent.copy.copied') : t('agent.copy.copy')}>
            <IconButton size="small" onClick={copy} sx={{ color: ok ? '#10B981' : '#6B6B7B', '&:hover': { color: '#701280' } }}>
                {ok ? <CheckIcon sx={{ fontSize: '0.9rem' }} /> : <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />}
            </IconButton>
        </Tooltip>
    );
};

// ─── Markdown Renderer ────────────────────────────────────────────────────────

const MarkdownText = ({ text, articleMode = false }) => {
    const lines = (text || '').split('\n');
    const elements = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (line.startsWith('## ')) {
            elements.push(<h3 key={i} style={{ fontSize: articleMode ? '1.05rem' : '0.95rem', fontWeight: 700, color: '#1C1C28', margin: articleMode ? '1.4em 0 0.5em' : '0.8em 0 0.3em', lineHeight: 1.4 }}>{line.slice(3)}</h3>);
        } else if (line.startsWith('# ')) {
            elements.push(<h2 key={i} style={{ fontSize: articleMode ? '1.2rem' : '1rem', fontWeight: 800, color: '#1C1C28', margin: articleMode ? '1.6em 0 0.6em' : '0.8em 0 0.3em', lineHeight: 1.3 }}>{line.slice(2)}</h2>);
        } else if (line.trim() === '') {
            if (articleMode) elements.push(<div key={i} style={{ height: '0.6em' }} />);
        } else {
            const inlineHtml = line
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/`(.*?)`/g, '<code style="background:rgba(112,18,128,0.10);padding:2px 5px;border-radius:4px;font-size:0.85em;font-family:monospace;color:#701280">$1</code>');
            elements.push(<p key={i} style={{ margin: articleMode ? '0 0 0.5em' : '0', lineHeight: 1.7, fontSize: articleMode ? '0.9rem' : 'inherit' }} dangerouslySetInnerHTML={{ __html: inlineHtml }} />);
        }
        i++;
    }
    return <>{elements}</>;
};

// ─── Tool Result Cards ────────────────────────────────────────────────────────

const LinkedInPostCard = ({ result }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const text = result.post_text || '';
    const preview = text.length > 220 ? text.slice(0, 220) + '…' : text;
    return (
        <Box sx={{ mt: 1.5, border: '1px solid rgba(10,102,194,0.15)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(10,102,194,0.04)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#0A66C2' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0A66C2', letterSpacing: '0.5px', textTransform: 'uppercase', flex: 1 }}>{t('agent.card.linkedin_post')}</Typography>
                <CopyBtn text={text} />
            </Box>
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ width: 38, height: 38, background: 'linear-gradient(135deg, #701280, #FF6B35)', fontSize: '0.85rem', fontWeight: 700 }}>A</Avatar>
                    <Box>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1C1C28' }}>{t('agent.card.your_profile')}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#6B6B7B' }}>{t('agent.card.marketing_now')}</Typography>
                    </Box>
                </Box>
                <Typography sx={{ fontSize: '0.875rem', color: '#1C1C28', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                    {expanded ? text : preview}
                </Typography>
                {text.length > 220 && (
                    <Typography onClick={() => setExpanded(!expanded)} sx={{ fontSize: '0.8rem', color: '#0A66C2', cursor: 'pointer', mt: 0.5, fontWeight: 600 }}>
                        {expanded ? t('agent.card.show_less') : t('agent.card.read_more')}
                    </Typography>
                )}
                {result.hashtags?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                        {result.hashtags.map((h, i) => (
                            <Chip key={i} label={h.startsWith('#') ? h : `#${h}`} size="small" sx={{ fontSize: '0.7rem', height: 20, background: 'rgba(10,102,194,0.07)', color: '#0A66C2', fontWeight: 600 }} />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const ArticleCard = ({ result }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const content = result.content || '';
    const preview = content.length > 320 ? content.slice(0, 320) + '…' : content;
    return (
        <Box sx={{ mt: 1.5, border: '1px solid rgba(255,107,53,0.15)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(255,107,53,0.04)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#FF6B35' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#FF6B35', letterSpacing: '0.5px', textTransform: 'uppercase', flex: 1 }}>{t('agent.card.article')}</Typography>
                <CopyBtn text={`${result.title || ''}\n\n${content}`} />
            </Box>
            <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1C1C28', mb: 1, lineHeight: 1.4 }}>{result.title}</Typography>
                {result.meta_description && (
                    <Typography sx={{ fontSize: '0.78rem', color: '#6B6B7B', mb: 1.5, fontStyle: 'italic', borderLeft: '3px solid rgba(255,107,53,0.3)', pl: 1.5 }}>
                        {result.meta_description}
                    </Typography>
                )}
                <Box sx={{ fontSize: '0.875rem', color: '#1C1C28' }}>
                    <MarkdownText text={expanded ? content : preview} articleMode />
                </Box>
                {content.length > 320 && (
                    <Typography onClick={() => setExpanded(!expanded)} sx={{ fontSize: '0.8rem', color: '#FF6B35', cursor: 'pointer', mt: 1, fontWeight: 600 }}>
                        {expanded ? t('agent.card.article_short') : t('agent.card.article_full')}
                    </Typography>
                )}
                {result.tags?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                        {result.tags.map((tag, i) => (
                            <Chip key={i} label={tag} size="small" sx={{ fontSize: '0.7rem', height: 20, background: 'rgba(255,107,53,0.07)', color: '#FF6B35', fontWeight: 600 }} />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const CampaignCard = ({ result }) => {
    const { t } = useTranslation();
    const [section, setSection] = useState('summary');
    const tabs = [
        { id: 'summary',  label: t('agent.campaign_tabs.summary') },
        { id: 'messages', label: t('agent.campaign_tabs.messages') },
        { id: 'calendar', label: t('agent.campaign_tabs.calendar') },
        { id: 'linkedin', label: t('agent.campaign_tabs.linkedin') },
        { id: 'articles', label: t('agent.campaign_tabs.articles') },
    ];
    return (
        <Box sx={{ mt: 1.5, border: '1px solid rgba(112,18,128,0.15)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(112,18,128,0.04)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: 'linear-gradient(135deg, #701280, #FF6B35)' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#701280', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t('agent.card.campaign')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', overflowX: 'auto', px: 2, pt: 1.5, gap: 0.5, borderBottom: '1px solid rgba(0,0,0,0.06)', '&::-webkit-scrollbar': { display: 'none' } }}>
                {tabs.map(tab => (
                    <Box key={tab.id} onClick={() => setSection(tab.id)} sx={{ px: 1.5, py: 0.7, borderRadius: '8px 8px 0 0', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.78rem', fontWeight: section === tab.id ? 600 : 400, color: section === tab.id ? '#701280' : '#6B6B7B', background: section === tab.id ? 'rgba(112,18,128,0.07)' : 'transparent', borderBottom: section === tab.id ? '2px solid #701280' : '2px solid transparent', transition: 'all 0.15s' }}>
                        {tab.label}
                    </Box>
                ))}
            </Box>
            <Box sx={{ p: 2, minHeight: 100 }}>
                {section === 'summary' && (
                    <Box>
                        <Typography sx={{ fontSize: '0.875rem', color: '#1C1C28', lineHeight: 1.65 }}>{result.summary || '—'}</Typography>
                        {result.channels && (
                            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(result.channels || []).map((ch, i) => (
                                    <Chip key={i} label={ch} size="small" sx={{ fontSize: '0.7rem', height: 20, background: 'rgba(112,18,128,0.07)', color: '#701280', fontWeight: 600 }} />
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
                {section === 'messages' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(result.key_messages || []).map((msg, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, background: 'rgba(112,18,128,0.04)', borderRadius: '10px' }}>
                                <Typography sx={{ fontSize: '0.85rem', color: '#701280', fontWeight: 700, minWidth: 20 }}>{i + 1}</Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: '#1C1C28', lineHeight: 1.6, flex: 1 }}>{msg}</Typography>
                                <CopyBtn text={msg} />
                            </Box>
                        ))}
                    </Box>
                )}
                {section === 'calendar' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(result.calendar?.weeks || []).map((w, i) => (
                            <Box key={i} sx={{ p: 1.5, background: '#f8f9fa', borderRadius: '10px' }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#701280', mb: 0.5 }}>{t('agent.card.week', { n: i + 1 })}</Typography>
                                <Typography sx={{ fontSize: '0.82rem', color: '#1C1C28', lineHeight: 1.6 }}>{typeof w === 'string' ? w : JSON.stringify(w)}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
                {section === 'linkedin' && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}><CopyBtn text={result.linkedin_post || ''} /></Box>
                        <Typography sx={{ fontSize: '0.875rem', color: '#1C1C28', lineHeight: 1.7, whiteSpace: 'pre-wrap', background: 'rgba(10,102,194,0.04)', p: 1.5, borderRadius: '10px' }}>
                            {result.linkedin_post || '—'}
                        </Typography>
                    </Box>
                )}
                {section === 'articles' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(result.article_titles || []).map((title, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, background: 'rgba(255,107,53,0.04)', borderRadius: '10px' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: '#FF6B35', fontWeight: 700 }}>✍️</Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: '#1C1C28', flex: 1, lineHeight: 1.5 }}>{title}</Typography>
                                <CopyBtn text={title} />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const GeneratedImageCard = ({ result }) => {
    const { t } = useTranslation();
    const handleDownload = () => {
        const a = document.createElement('a');
        a.href = result.image_url;
        a.download = 'admind-image.png';
        a.target = '_blank';
        a.click();
    };
    if (result?.service_error) {
        return (
            <Box sx={{ mt: 1.5, border: '1px solid rgba(255,107,53,0.2)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
                <Box sx={{ px: 2, py: 1.2, background: 'rgba(255,107,53,0.05)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#FF6B35' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#FF6B35', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t('agent.card.image_error_label')}</Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#6B6B7B' }}>{t('agent.card.image_error_msg')}</Typography>
                </Box>
            </Box>
        );
    }
    return (
        <Box sx={{ mt: 1.5, border: '1px solid rgba(112,18,128,0.15)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(112,18,128,0.04)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#701280' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#701280', letterSpacing: '0.5px', textTransform: 'uppercase', flex: 1 }}>
                    {t('agent.card.image_generated')}
                </Typography>
                <Tooltip title={t('agent.card.image_download')}>
                    <IconButton size="small" onClick={handleDownload} sx={{ color: '#701280', p: 0.5, '&:hover': { background: 'rgba(112,18,128,0.08)' } }}>
                        <DownloadIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2 }}>
                <Box
                    component="img"
                    src={result.image_url}
                    alt={t('agent.card.image_alt')}
                    sx={{ width: '100%', borderRadius: '10px', display: 'block', cursor: 'pointer' }}
                    onClick={() => window.open(result.image_url, '_blank')}
                />
                {result.prompt && (
                    <Typography sx={{ fontSize: '0.72rem', color: '#9B9BA8', mt: 1, lineHeight: 1.5, fontStyle: 'italic' }}>
                        {result.prompt.length > 120 ? result.prompt.slice(0, 120) + '…' : result.prompt}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const ImageConceptsCard = ({ result }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ mt: 1.5, border: '1px solid rgba(16,185,129,0.15)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(16,185,129,0.04)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#10B981' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#10B981', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t('agent.card.prompt_label')}</Typography>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {result.style_notes && <Typography sx={{ fontSize: '0.78rem', color: '#6B6B7B', fontStyle: 'italic', mb: 0.5 }}>💡 {result.style_notes}</Typography>}
                {(result.prompts || []).map((prompt, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, p: 1.5, background: 'rgba(16,185,129,0.04)', borderRadius: '10px' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#10B981', minWidth: 20 }}>{i + 1}</Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: '#4A4A5A', lineHeight: 1.6, flex: 1, fontFamily: 'monospace' }}>{prompt}</Typography>
                        <CopyBtn text={prompt} />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const CalendarSavedCard = ({ result }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ mt: 1.5, border: '1px solid rgba(112,18,128,0.15)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(112,18,128,0.04)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#701280' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#701280', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    📅 {t('agent.card.calendar_added', { count: result.count || 0 })}
                </Typography>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {(result.saved || []).map((e, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, background: e.error ? 'rgba(255,107,53,0.05)' : 'rgba(112,18,128,0.04)', borderRadius: '8px' }}>
                        <Typography sx={{ fontSize: '0.8rem', flex: 1, color: '#1C1C28' }}>{e.title}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#9B9BA8' }}>{e.scheduled_date}</Typography>
                        {e.error ? <Typography sx={{ fontSize: '0.72rem', color: '#FF6B35' }}>⚠️</Typography> : <Typography sx={{ fontSize: '0.72rem', color: '#10B981' }}>✓</Typography>}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const VideoCard = ({ result, onRetry, onVideoBlocked }) => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    if (result?.video_blocked) {
        const reason = result.reason;
        return (
            <Box sx={{ mt: 1.5, border: '1px solid rgba(112,18,128,0.12)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(112,18,128,0.03)' }}>
                    <VideocamIcon sx={{ fontSize: '0.9rem', color: '#701280' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#701280', letterSpacing: '0.5px', textTransform: 'uppercase' }}>🎬 Video · VEO 2</Typography>
                </Box>
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.6rem' }}>🔒</Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: '#4A4A5A', lineHeight: 1.6, maxWidth: 280 }}>
                        {reason === 'not_pro' && t('agent.video_blocked.not_pro_msg')}
                        {reason === 'pro_light_no_video' && t('agent.video_blocked.pro_light_msg')}
                        {reason === 'pro_max_limit_exceeded' && t('agent.video_blocked.pro_max_msg')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {(reason === 'not_pro' || reason === 'pro_light_no_video') && (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => onVideoBlocked && onVideoBlocked(reason, 'pricing')}
                                sx={{ background: 'linear-gradient(135deg,#701280,#8E3A9E)', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                            >
                                {t('agent.video_blocked.btn_pro_max')}
                            </Button>
                        )}
                        {(reason === 'pro_light_no_video' || reason === 'pro_max_limit_exceeded') && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onVideoBlocked && onVideoBlocked(reason, 'payperuse')}
                                sx={{ borderColor: 'rgba(112,18,128,0.4)', color: '#701280', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', '&:hover': { background: 'rgba(112,18,128,0.05)' } }}
                            >
                                {t('agent.video_blocked.btn_pay_per_use')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    if (result?.service_error || result?.error) {
        return (
            <Box sx={{ mt: 1.5, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(0,0,0,0.02)' }}>
                    <VideocamIcon sx={{ fontSize: '0.9rem', color: '#9B9BA8' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9B9BA8', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t('agent.card.video_error_label')}</Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '0.875rem', color: '#4A4A5A', lineHeight: 1.65, flex: 1 }}>
                        {t('agent.card.video_error_msg')}
                    </Typography>
                    {onRetry && (
                        <Button size="small" variant="outlined" onClick={onRetry} sx={{ flexShrink: 0, borderRadius: '8px', textTransform: 'none', borderColor: 'rgba(112,18,128,0.3)', color: '#701280', '&:hover': { background: 'rgba(112,18,128,0.05)' } }}>
                            {t('agent.retry')}
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }

    const togglePlay = () => {
        if (!videoRef.current) return;
        if (playing) { videoRef.current.pause(); setPlaying(false); }
        else { videoRef.current.play(); setPlaying(true); }
    };

    return (
        <Box sx={{ mt: 1.5, border: '1px solid rgba(112,18,128,0.15)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(112,18,128,0.04)' }}>
                <VideocamIcon sx={{ fontSize: '0.9rem', color: '#701280' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#701280', letterSpacing: '0.5px', textTransform: 'uppercase', flex: 1 }}>
                    {result?.generator === 'sora' ? '🎬 Video · Sora' : `🎬 Video · VEO 2${result?.used_image ? ' · Image-to-Video' : ''}`}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#9B9BA8' }}>{result?.duration || 8}s · {result?.aspect_ratio || '16:9'}</Typography>
            </Box>
            <Box sx={{ position: 'relative', background: '#000', cursor: 'pointer' }} onClick={togglePlay}>
                <Box
                    component="video"
                    ref={videoRef}
                    src={result?.video_url ? (result.video_url.startsWith('http') ? result.video_url : '/' + result.video_url.replace(/^\/+/, '')) : undefined}
                    loop
                    onEnded={() => setPlaying(false)}
                    sx={{ width: '100%', maxHeight: 360, display: 'block', objectFit: 'contain' }}
                />
                {!playing && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                            <PlayArrowIcon sx={{ fontSize: '2rem', color: '#701280', ml: 0.5 }} />
                        </Box>
                    </Box>
                )}
            </Box>
            {result?.prompt && (
                <Box sx={{ px: 2, py: 1.2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9B9BA8', fontFamily: 'monospace', lineHeight: 1.5 }}>
                        🎬 {result.prompt.length > 120 ? result.prompt.slice(0, 120) + '…' : result.prompt}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

const ToolResultCard = ({ tool_used, tool_result, onRetry, onVideoBlocked }) => {
    const { t } = useTranslation();
    if (!tool_used || !tool_result) return null;
    if ((tool_result.error || tool_result.service_error) && tool_used !== 'generate_video') return (
        <Box sx={{ mt: 1.5, p: 2, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#4A4A5A', flex: 1 }}>
                {t('agent.error_generic')}
            </Typography>
            {onRetry && (
                <Button size="small" variant="outlined" onClick={onRetry} sx={{ flexShrink: 0, borderRadius: '8px', textTransform: 'none', borderColor: 'rgba(112,18,128,0.3)', color: '#701280', '&:hover': { background: 'rgba(112,18,128,0.05)' } }}>
                    {t('agent.retry')}
                </Button>
            )}
        </Box>
    );
    if (tool_used === 'create_linkedin_post') return <LinkedInPostCard result={tool_result} />;
    if (tool_used === 'write_article') return <ArticleCard result={tool_result} />;
    if (tool_used === 'build_campaign') return <CampaignCard result={tool_result} />;
    if (tool_used === 'generate_image') return <GeneratedImageCard result={tool_result} />;
    if (tool_used === 'create_image_concepts') return <ImageConceptsCard result={tool_result} />;
    if (tool_used === 'generate_video') return <VideoCard result={tool_result} onRetry={onRetry} onVideoBlocked={onVideoBlocked} />;
    if (tool_used === 'save_to_calendar') return <CalendarSavedCard result={tool_result} />;
    return null;
};

// ─── Social Modal (LinkedIn only) ─────────────────────────────────────────────

const SocialModal = ({ open, onClose, text }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [status, setStatus] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [result, setResult] = useState(null);

    const fetchStatus = () => {
        setLoading(true);
        api.get('/api/openai/linkedin/status/')
            .then(r => setStatus(r.data))
            .catch(() => setStatus({ connected: false, configured: false }))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open) { setResult(null); fetchStatus(); }
    }, [open]);

    const handleConnect = async () => {
        setConnecting(true);
        try {
            const r = await api.get('/api/openai/linkedin/auth/');
            window.location.href = r.data.auth_url;
        } catch (e) {
            setResult({ error: e?.response?.data?.error || t('agent.error_generic') });
        } finally {
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        await api.delete('/api/openai/linkedin/status/').catch(() => {});
        setStatus({ connected: false, configured: status?.configured });
    };

    const publish = async () => {
        setPublishing(true);
        try {
            await api.post('/api/openai/linkedin/publish/', { text });
            setResult({ success: true });
        } catch (e) {
            setResult({ error: e?.response?.data?.error || t('agent.error_generic') });
        } finally {
            setPublishing(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: { xs: '90vw', sm: 420 }, background: '#fff', borderRadius: '18px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', outline: 'none', overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '6px', background: '#0A66C2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.75rem', lineHeight: 1 }}>in</Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1C1C28' }}>{t('agent.social.linkedin_title')}</Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#6B6B7B' }}><CloseIcon fontSize="small" /></IconButton>
                </Box>
                <Box sx={{ p: 3 }}>
                    {loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} sx={{ color: '#0A66C2' }} /></Box>}

                    {!loading && status && !status.connected && !result && (
                        <Box sx={{ textAlign: 'center', py: 1 }}>
                            <Box sx={{ width: 56, height: 56, borderRadius: '14px', background: 'rgba(10,102,194,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                                <Typography sx={{ fontSize: '1.6rem', lineHeight: 1 }}>🔗</Typography>
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1C1C28', mb: 0.5 }}>{t('agent.social.linkedin_connect_title')}</Typography>
                            <Typography sx={{ color: '#6B6B7B', fontSize: '0.84rem', mb: 3, lineHeight: 1.5 }}>{t('agent.social.linkedin_connect_msg')}</Typography>
                            {!status.configured && (
                                <Typography sx={{ color: '#FF6B35', fontSize: '0.8rem', mb: 2 }}>{t('agent.social.linkedin_not_configured')}</Typography>
                            )}
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={connecting || !status.configured}
                                // disabled={true}
                                onClick={handleConnect}
                                sx={{ background: '#0A66C2', '&:hover': { background: '#004182' }, borderRadius: '10px', textTransform: 'none', fontWeight: 600, py: 1.2 }}
                            >
                                {connecting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('agent.social.linkedin_connect_btn')}
                            </Button>
                        </Box>
                    )}

                    {!loading && status?.connected && !result && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, p: 1.5, background: 'rgba(10,102,194,0.05)', borderRadius: '10px', border: '1px solid rgba(10,102,194,0.12)' }}>
                                {status.picture && <Box component="img" src={status.picture} alt="" sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#1C1C28' }}>{status.name}</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#6B6B7B' }}>{status.email}</Typography>
                                </Box>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', flexShrink: 0 }} />
                            </Box>
                            <Box sx={{ background: 'rgba(10,102,194,0.04)', border: '1px solid rgba(10,102,194,0.1)', borderRadius: '10px', p: 1.5, mb: 2.5, maxHeight: 120, overflowY: 'auto' }}>
                                <Typography sx={{ fontSize: '0.8rem', color: '#4A4A5A', lineHeight: 1.6 }}>{text?.slice(0, 300)}{text?.length > 300 ? '…' : ''}</Typography>
                            </Box>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={publishing}
                                onClick={publish}
                                sx={{ background: '#0A66C2', '&:hover': { background: '#004182' }, borderRadius: '10px', textTransform: 'none', fontWeight: 600, py: 1.2, mb: 1 }}
                            >
                                {publishing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('agent.social.linkedin_publish_btn')}
                            </Button>
                            <Button
                                fullWidth
                                variant="text"
                                onClick={handleDisconnect}
                                sx={{ color: '#9CA3AF', fontSize: '0.78rem', textTransform: 'none', py: 0.5 }}
                            >
                                {t('agent.social.linkedin_disconnect')}
                            </Button>
                        </Box>
                    )}

                    {result?.success && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography sx={{ fontSize: '2rem', mb: 1 }}>✅</Typography>
                            <Typography sx={{ fontWeight: 700, color: '#10B981', mb: 0.5 }}>{t('agent.social.published_title')}</Typography>
                            <Typography sx={{ color: '#6B6B7B', fontSize: '0.85rem' }}>{t('agent.social.linkedin_published_msg')}</Typography>
                            <Button onClick={onClose} variant="outlined" sx={{ mt: 2, borderColor: '#0A66C2', color: '#0A66C2', borderRadius: '10px', textTransform: 'none' }}>{t('agent.social.close')}</Button>
                        </Box>
                    )}
                    {result?.error && (
                        <Box sx={{ background: 'rgba(255,107,53,0.06)', border: '1px solid rgba(255,107,53,0.15)', borderRadius: '10px', p: 1.5 }}>
                            <Typography sx={{ fontSize: '0.85rem', color: '#FF6B35' }}>⚠️ {result.error}</Typography>
                            <Button onClick={() => setResult(null)} variant="text" size="small" sx={{ mt: 0.5, color: '#6B6B7B', textTransform: 'none', fontSize: '0.78rem', p: 0 }}>{t('agent.social.close')}</Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

// ─── Notion Modal ─────────────────────────────────────────────────────────────

const NotionModal = ({ open, onClose, toolUsed, toolResult, title, imageUrl }) => {
    const { t } = useTranslation();
    const [token, setToken] = useState(() => localStorage.getItem('admind_notion_token') || '');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const send = async () => {
        if (!token.trim()) return;
        localStorage.setItem('admind_notion_token', token.trim());
        setLoading(true);
        try {
            const r = await api.post('/api/openai/notion-send/', {
                token: token.trim(),
                content_type: toolUsed,
                content: toolResult,
                title: title || 'Contenuto Admind',
                image_url: imageUrl || '',
            });
            setResult({ success: true, url: r.data.url });
        } catch (e) {
            setResult({ error: e?.response?.data?.error || t('agent.error_generic') });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => { setResult(null); onClose(); };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: { xs: '90vw', sm: 440 }, background: '#fff', borderRadius: '18px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', outline: 'none', overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1C1C28' }}>{t('agent.notion.title')}</Typography>
                    <IconButton size="small" onClick={handleClose} sx={{ color: '#6B6B7B' }}><CloseIcon fontSize="small" /></IconButton>
                </Box>
                <Box sx={{ p: 3 }}>
                    {!result ? (
                        <Box>
                            <Typography sx={{ fontSize: '0.85rem', color: '#6B6B7B', mb: 2, lineHeight: 1.6 }}>
                                {t('agent.notion.instruction')}<br />
                                <Typography component="a" href="https://www.notion.so/my-integrations" target="_blank" sx={{ fontSize: '0.8rem', color: '#701280', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    {t('agent.notion.create_link')}
                                </Typography>
                            </Typography>
                            <TextField
                                fullWidth
                                label="Notion Integration Token"
                                placeholder="secret_xxxxxxxxxxxxxxxx"
                                value={token}
                                onChange={e => setToken(e.target.value)}
                                size="small"
                                type="password"
                                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                            <Typography sx={{ fontSize: '0.78rem', color: '#9B9BA8', mb: 2.5 }}>
                                {t('agent.notion.token_note')}
                            </Typography>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={!token.trim() || loading}
                                onClick={send}
                                sx={{ background: '#000', borderRadius: '10px', textTransform: 'none', fontWeight: 600, py: 1.2, '&:hover': { background: '#333' } }}
                            >
                                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('agent.notion.send')}
                            </Button>
                        </Box>
                    ) : result.success ? (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography sx={{ fontSize: '2rem', mb: 1 }}>✅</Typography>
                            <Typography sx={{ fontWeight: 700, color: '#10B981', mb: 0.5 }}>{t('agent.notion.saved_title')}</Typography>
                            <Typography sx={{ color: '#6B6B7B', fontSize: '0.85rem', mb: 2 }}>{t('agent.notion.saved_msg')}</Typography>
                            {result.url && (
                                <Button component="a" href={result.url} target="_blank" variant="outlined" endIcon={<OpenInNewIcon />} sx={{ borderColor: '#000', color: '#000', borderRadius: '10px', textTransform: 'none', mr: 1 }}>
                                    {t('agent.notion.open')}
                                </Button>
                            )}
                            <Button onClick={handleClose} variant="text" sx={{ color: '#6B6B7B', textTransform: 'none' }}>{t('agent.notion.close')}</Button>
                        </Box>
                    ) : (
                        <Box sx={{ background: 'rgba(255,107,53,0.06)', border: '1px solid rgba(255,107,53,0.15)', borderRadius: '10px', p: 2 }}>
                            <Typography sx={{ fontSize: '0.85rem', color: '#FF6B35', mb: 1.5 }}>⚠️ {result.error}</Typography>
                            <Button size="small" onClick={() => setResult(null)} sx={{ color: '#701280', textTransform: 'none' }}>{t('agent.notion.retry')}</Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

// ─── Image Preview ────────────────────────────────────────────────────────────

const ImagePreview = ({ imageUrl }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ mt: 1.5, borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(16,185,129,0.04)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#10B981' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#10B981', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t('agent.card.image_generated2')}</Typography>
            </Box>
            <Box component="img" src={imageUrl} alt={t('agent.card.image_alt')} sx={{ width: '100%', display: 'block', maxHeight: 400, objectFit: 'cover' }} />
        </Box>
    );
};

// ─── Schedule Dialog ──────────────────────────────────────────────────────────

const getTomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
};

const CONTENT_TYPE_MAP = {
    create_linkedin_post: 'post',
    write_article: 'article',
    build_campaign: 'idea',
    create_image_concepts: 'idea',
    generate_image: 'image',
};

const getScheduleTitle = (toolUsed, toolResult) => {
    if (toolUsed === 'create_linkedin_post') return `Post LinkedIn — ${new Date().toLocaleDateString('it-IT')}`;
    if (toolUsed === 'write_article') return toolResult?.title || `Articolo — ${new Date().toLocaleDateString('it-IT')}`;
    if (toolUsed === 'build_campaign') return `Campagna: ${toolResult?.summary?.slice(0, 60) || new Date().toLocaleDateString('it-IT')}`;
    if (toolUsed === 'create_image_concepts') return `Prompt Immagini — ${new Date().toLocaleDateString('it-IT')}`;
    return `Contenuto — ${new Date().toLocaleDateString('it-IT')}`;
};

const getScheduleContent = (toolUsed, toolResult) => {
    if (toolUsed === 'create_linkedin_post') {
        const tags = (toolResult?.hashtags || []).join(' ');
        return `${toolResult?.post_text || ''}\n\n${tags}`;
    }
    if (toolUsed === 'write_article') return `${toolResult?.title || ''}\n\n${toolResult?.content || ''}`;
    if (toolUsed === 'build_campaign') return `${toolResult?.summary || ''}\n\n${toolResult?.linkedin_post || ''}`;
    if (toolUsed === 'create_image_concepts') return (toolResult?.prompts || []).join('\n\n');
    return '';
};

const ScheduleDialog = ({ open, onClose, toolUsed, toolResult, generatedImageUrl }) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [date, setDate] = useState(getTomorrow());
    const [contentType, setContentType] = useState('idea');
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    const CONTENT_TYPES = [
        { value: 'post',    label: t('agent.schedule.types.post') },
        { value: 'article', label: t('agent.schedule.types.article') },
        { value: 'idea',    label: t('agent.schedule.types.idea') },
        { value: 'image',   label: t('agent.schedule.types.image') },
        { value: 'video',   label: t('agent.schedule.types.video') },
    ];

    useEffect(() => {
        if (open) {
            setTitle(getScheduleTitle(toolUsed, toolResult));
            setContentType(CONTENT_TYPE_MAP[toolUsed] || 'idea');
            setDate(getTomorrow());
        }
    }, [open, toolUsed, toolResult]);

    const handleSave = async () => {
        if (!title.trim() || !date) return;
        setSaving(true);
        const imageUrl = generatedImageUrl || toolResult?.image_url || '';
        try {
            await api.post('/api/openai/calendar/', {
                title: title.trim(),
                content_type: contentType,
                scheduled_date: date,
                content_text: getScheduleContent(toolUsed, toolResult),
                image_url: imageUrl,
                status: 'planned',
            });
            setSnack({ open: true, msg: t('agent.schedule.snack_ok'), severity: 'success' });
            setTimeout(() => onClose(), 1200);
        } catch {
            setSnack({ open: true, msg: t('agent.schedule.snack_err'), severity: 'error' });
        }
        setSaving(false);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', p: 0.5 } }}>
                <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, background: 'linear-gradient(135deg, #701280, #A78BFA)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <EventIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1C1C28' }}>{t('agent.schedule.title')}</Typography>
                        <Typography sx={{ fontSize: '0.73rem', color: '#6B6B7B' }}>{t('agent.schedule.subtitle')}</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField fullWidth size="small" label={t('agent.schedule.event_title')} value={title} onChange={e => setTitle(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    <TextField fullWidth size="small" label={t('agent.schedule.date')} type="date" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().split('T')[0] }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('agent.schedule.content_type')}</InputLabel>
                        <Select value={contentType} label={t('agent.schedule.content_type')} onChange={e => setContentType(e.target.value)} sx={{ borderRadius: '10px' }}>
                            {CONTENT_TYPES.map(ct => <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={onClose} sx={{ textTransform: 'none', color: '#6B6B7B', borderRadius: '10px' }}>{t('agent.schedule.cancel')}</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving || !title.trim() || !date}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                        sx={{ textTransform: 'none', borderRadius: '10px', px: 2.5, background: 'linear-gradient(135deg, #701280, #A78BFA)', '&:hover': { background: 'linear-gradient(135deg, #5B3DE8, #701280)' } }}>
                        {saving ? t('agent.schedule.saving') : t('agent.schedule.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} sx={{ borderRadius: '12px' }}>{snack.msg}</Alert>
            </Snackbar>
        </>
    );
};

// ─── Action Bar ───────────────────────────────────────────────────────────────

const ActionBar = ({ toolUsed, toolResult }) => {
    const { t } = useTranslation();
    const [imgState, setImgState] = useState('idle');
    const [imgUrl, setImgUrl] = useState(null);
    const [socialOpen, setSocialOpen] = useState(false);
    const [notionOpen, setNotionOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [dlAnchor, setDlAnchor] = useState(null);
    const [dlLoading, setDlLoading] = useState(false);

    if (!toolUsed || !toolResult || toolResult.error) return null;

    const getTextForShare = () => {
        if (toolUsed === 'create_linkedin_post') return toolResult.post_text || '';
        if (toolUsed === 'write_article') return `${toolResult.title || ''}\n\n${toolResult.content || ''}`;
        if (toolUsed === 'build_campaign') return toolResult.linkedin_post || toolResult.summary || '';
        if (toolUsed === 'create_image_concepts') return (toolResult.prompts || []).join('\n\n');
        return '';
    };

    const getDownloadText = () => {
        if (toolUsed === 'create_linkedin_post') {
            const tags = (toolResult.hashtags || []).map(h => h.startsWith('#') ? h : `#${h}`).join(' ');
            return `POST LINKEDIN\n${'='.repeat(40)}\n\n${toolResult.post_text || ''}\n\n${tags}`;
        }
        if (toolUsed === 'write_article') {
            return `${toolResult.title || 'Articolo'}\n${'='.repeat(40)}\n\n${toolResult.meta_description || ''}\n\n${toolResult.content || ''}`;
        }
        if (toolUsed === 'build_campaign') {
            const msgs = (toolResult.key_messages || []).map((m, i) => `${i + 1}. ${m}`).join('\n');
            return `CAMPAGNA MARKETING\n${'='.repeat(40)}\n\nSTRATEGIA:\n${toolResult.summary || ''}\n\nMESSAGGI CHIAVE:\n${msgs}\n\nPOST LINKEDIN:\n${toolResult.linkedin_post || ''}`;
        }
        if (toolUsed === 'create_image_concepts') {
            return `PROMPT IMMAGINI MARKETING\n${'='.repeat(40)}\n\n${(toolResult.prompts || []).map((p, i) => `${i + 1}. ${p}`).join('\n\n')}`;
        }
        return '';
    };

    const getImgPrompt = () => {
        if (toolUsed === 'create_linkedin_post') return `Professional marketing visual for LinkedIn post about: ${(toolResult.post_text || '').slice(0, 200)}. Clean, modern corporate style, no text overlay.`;
        if (toolUsed === 'write_article') return `Professional blog header image for article: "${toolResult.title || ''}". Modern, clean editorial style, no text.`;
        if (toolUsed === 'build_campaign') return `Marketing campaign visual for: ${toolResult.summary?.slice(0, 200) || ''}. Professional, brand-focused, vibrant colors, no text.`;
        return null;
    };

    const getNotionTitle = () => {
        if (toolUsed === 'create_linkedin_post') return `Post LinkedIn — ${new Date().toLocaleDateString('it-IT')}`;
        if (toolUsed === 'write_article') return toolResult.title || `Articolo — ${new Date().toLocaleDateString('it-IT')}`;
        if (toolUsed === 'build_campaign') return `Campagna Marketing — ${new Date().toLocaleDateString('it-IT')}`;
        if (toolUsed === 'create_image_concepts') return `Prompt Immagini — ${new Date().toLocaleDateString('it-IT')}`;
        return 'Contenuto Admind';
    };

    const getFilename = () => {
        if (toolUsed === 'write_article') return (toolResult.title || 'articolo').slice(0, 50);
        if (toolUsed === 'create_linkedin_post') return `post-linkedin-${new Date().toISOString().split('T')[0]}`;
        if (toolUsed === 'build_campaign') return `campagna-${new Date().toISOString().split('T')[0]}`;
        return `admind-${toolUsed}`;
    };

    const download = async (fmt) => {
        setDlAnchor(null);
        setDlLoading(true);
        const text = getDownloadText();
        const filename = getFilename();
        if (fmt === 'txt') {
            const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `${filename}.txt`; a.click();
            URL.revokeObjectURL(url);
            setDlLoading(false);
            return;
        }
        try {
            const resp = await api.post('/api/openai/chat-export/', { content: text, format: fmt, filename }, { responseType: 'blob' });
            const extMap = { word: 'docx', excel: 'xlsx', pptx: 'pptx', pdf: 'pdf' };
            const ext = extMap[fmt] || fmt;
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url; a.download = `${filename}.${ext}`; a.click();
            URL.revokeObjectURL(url);
        } catch {
            alert(t('agent.action.error_download'));
        }
        setDlLoading(false);
    };

    const generateImage = async () => {
        const prompt = getImgPrompt();
        if (!prompt) return;
        setImgState('loading');
        try {
            const r = await api.post('/api/openai/agent-image/', { prompt });
            setImgUrl(r.data.image_url);
            setImgState('done');
        } catch {
            setImgState('error');
        }
    };

    const canGenImg = toolUsed === 'create_linkedin_post' || toolUsed === 'write_article' || toolUsed === 'build_campaign';
    const canPublish = toolUsed === 'create_linkedin_post' || toolUsed === 'build_campaign';
    const btnStyle = { fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', px: 1.5, py: 0.7, gap: 0.6 };

    return (
        <>
            <Box sx={{ mt: 2, borderTop: '1px dashed rgba(112,18,128,0.15)', pt: 1.5 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9B9BA8', letterSpacing: '0.6px', textTransform: 'uppercase', mb: 1 }}>
                    {t('agent.action.what_next')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    <Button size="small" variant="outlined"
                        startIcon={dlLoading ? <CircularProgress size={12} color="inherit" /> : <DownloadIcon sx={{ fontSize: '0.9rem !important' }} />}
                        endIcon={<ArrowDropDownIcon sx={{ fontSize: '1rem !important', ml: -0.5 }} />}
                        onClick={e => setDlAnchor(e.currentTarget)}
                        disabled={dlLoading}
                        sx={{ ...btnStyle, borderColor: 'rgba(0,0,0,0.12)', color: '#4A4A5A', '&:hover': { borderColor: '#701280', color: '#701280', background: 'rgba(112,18,128,0.04)' } }}>
                        {dlLoading ? t('agent.action.downloading') : t('agent.action.download')}
                    </Button>
                    <Menu anchorEl={dlAnchor} open={Boolean(dlAnchor)} onClose={() => setDlAnchor(null)}
                        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', minWidth: 170 } }}>
                        {toolUsed === 'write_article' && <MenuItem onClick={() => download('pptx')} sx={{ fontSize: '0.83rem', gap: 1 }}>📊 PowerPoint (.pptx)</MenuItem>}
                        <MenuItem onClick={() => download('word')} sx={{ fontSize: '0.83rem', gap: 1 }}>📄 Word (.docx)</MenuItem>
                        {(toolUsed === 'write_article' || toolUsed === 'build_campaign') && <MenuItem onClick={() => download('excel')} sx={{ fontSize: '0.83rem', gap: 1 }}>📋 Excel (.xlsx)</MenuItem>}
                        <MenuItem onClick={() => download('pdf')} sx={{ fontSize: '0.83rem', gap: 1 }}>📕 PDF (.pdf)</MenuItem>
                        <MenuItem onClick={() => download('txt')} sx={{ fontSize: '0.83rem', gap: 1 }}>📝 Testo (.txt)</MenuItem>
                    </Menu>

                    {canGenImg && (
                        <Button size="small" variant="outlined" startIcon={<ImageIcon sx={{ fontSize: '0.9rem !important' }} />}
                            onClick={generateImage} disabled={imgState === 'loading' || imgState === 'done'}
                            sx={{ ...btnStyle, borderColor: 'rgba(16,185,129,0.3)', color: '#10B981', '&:hover': { borderColor: '#10B981', background: 'rgba(16,185,129,0.04)' }, '&:disabled': { color: '#10B981', opacity: 0.5 } }}>
                            {imgState === 'loading' ? <CircularProgress size={12} sx={{ color: '#10B981' }} /> : imgState === 'done' ? t('agent.action.image_done') : t('agent.action.generate_image')}
                        </Button>
                    )}

                    {canPublish && (
                        <Button size="small" variant="outlined" startIcon={<ShareIcon sx={{ fontSize: '0.9rem !important' }} />} onClick={() => setSocialOpen(true)}
                            sx={{ ...btnStyle, borderColor: 'rgba(10,102,194,0.3)', color: '#0A66C2', '&:hover': { borderColor: '#0A66C2', background: 'rgba(10,102,194,0.04)' } }}>
                            {t('agent.action.publish')}
                        </Button>
                    )}

                    <Button size="small" variant="outlined" startIcon={<CalendarMonthIcon sx={{ fontSize: '0.9rem !important' }} />} onClick={() => setScheduleOpen(true)}
                        sx={{ ...btnStyle, borderColor: 'rgba(112,18,128,0.3)', color: '#701280', '&:hover': { borderColor: '#701280', background: 'rgba(112,18,128,0.04)' } }}>
                        {t('agent.action.schedule')}
                    </Button>

                    <Button size="small" variant="outlined" startIcon={<NotesIcon sx={{ fontSize: '0.9rem !important' }} />} onClick={() => setNotionOpen(true)}
                        sx={{ ...btnStyle, borderColor: 'rgba(0,0,0,0.15)', color: '#1C1C28', '&:hover': { borderColor: '#000', background: 'rgba(0,0,0,0.03)' } }}>
                        {t('agent.action.notion')}
                    </Button>
                </Box>
            </Box>

            {imgState === 'error' && (
                <Typography sx={{ fontSize: '0.75rem', color: '#FF6B35', mt: 0.5 }}>{t('agent.action.image_error')}</Typography>
            )}
            {imgState === 'done' && imgUrl && <ImagePreview imageUrl={imgUrl} />}

            <SocialModal open={socialOpen} onClose={() => setSocialOpen(false)} text={getTextForShare()} />
            <NotionModal open={notionOpen} onClose={() => setNotionOpen(false)} toolUsed={toolUsed} toolResult={toolResult} title={getNotionTitle()} imageUrl={imgUrl} />
            <ScheduleDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} toolUsed={toolUsed} toolResult={toolResult} generatedImageUrl={imgUrl} />
        </>
    );
};

// ─── Animated Agent Blob ───────────────────────────────────────────────────────

const agentBlobKeyframes = `
@keyframes blobMorph {
  0%,100% { border-radius: 62% 38% 46% 54% / 60% 44% 56% 40%; }
  25%  { border-radius: 45% 55% 52% 48% / 50% 58% 42% 50%; }
  50%  { border-radius: 55% 45% 40% 60% / 44% 52% 48% 56%; }
  75%  { border-radius: 42% 58% 58% 42% / 56% 40% 60% 44%; }
}
@keyframes blobGlow {
  0%,100% { box-shadow: 0 0 14px 3px rgba(112,18,128,0.45), 0 0 30px 6px rgba(112,18,128,0.18); }
  50%      { box-shadow: 0 0 20px 5px rgba(112,18,128,0.65), 0 0 44px 10px rgba(112,18,128,0.28); }
}
`;

function AgentBlob({ size = 46 }) {
    return (
        <>
            <style>{agentBlobKeyframes}</style>
            <Box sx={{
                width: size, height: size, flexShrink: 0,
                background: 'linear-gradient(145deg, #1a0a3c 0%, #3a1580 45%, #701280 100%)',
                animation: 'blobMorph 8s ease-in-out infinite, blobGlow 4s ease-in-out infinite',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'default', userSelect: 'none',
                willChange: 'border-radius, box-shadow',
            }}>
                <Typography sx={{
                    fontSize: '0.42rem', fontWeight: 800, letterSpacing: '0.03em',
                    background: 'linear-gradient(135deg, #E0CFFF, #fff)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    lineHeight: 1, textTransform: 'uppercase',
                }}>Agent</Typography>
            </Box>
        </>
    );
}

// ─── Agent Question ────────────────────────────────────────────────────────────

const AgentQuestion = ({ content, toolUsed, toolResult, onSend, onSchedule }) => {
    const { t } = useTranslation();

    const SUGGESTION_CHIPS = {
        create_linkedin_post: [
            { label: t('agent.suggestions.change_tone'), message: t('agent.suggestions.change_tone_msg') },
            { label: t('agent.suggestions.lengthen'), message: t('agent.suggestions.lengthen_msg') },
            { label: t('agent.suggestions.rewrite'), message: t('agent.suggestions.rewrite_msg') },
            { label: t('agent.suggestions.add_to_cal'), action: 'schedule' },
        ],
        write_article: [
            { label: t('agent.suggestions.shorten'), message: t('agent.suggestions.shorten_msg') },
            { label: t('agent.suggestions.add_examples'), message: t('agent.suggestions.add_examples_msg') },
            { label: t('agent.suggestions.change_tone2'), message: t('agent.suggestions.change_tone2_msg') },
            { label: t('agent.suggestions.add_to_cal'), action: 'schedule' },
        ],
        build_campaign: [
            { label: t('agent.suggestions.save_cal'), action: 'schedule' },
            { label: t('agent.suggestions.expand_strategy'), message: t('agent.suggestions.expand_strategy_msg') },
            { label: t('agent.suggestions.add_channels'), message: t('agent.suggestions.add_channels_msg') },
        ],
        create_image_concepts: [
            { label: t('agent.suggestions.gen_first'), message: t('agent.suggestions.gen_first_msg') },
            { label: t('agent.suggestions.diff_style'), message: t('agent.suggestions.diff_style_msg') },
            { label: t('agent.suggestions.edit_prompts'), message: t('agent.suggestions.edit_prompts_msg') },
            { label: t('agent.suggestions.add_to_cal'), action: 'schedule' },
        ],
        generate_video: [
            { label: t('agent.suggestions.download_video'), message: null, action: 'download_video' },
            { label: t('agent.suggestions.create_variant'), message: t('agent.suggestions.create_variant_msg') },
            { label: t('agent.suggestions.create_post'), message: t('agent.suggestions.create_post_msg') },
            { label: t('agent.suggestions.add_to_cal'), action: 'schedule' },
        ],
        save_to_calendar: [
            { label: t('agent.suggestions.go_to_cal'), message: null, href: '/calendar' },
            { label: t('agent.suggestions.add_more'), message: t('agent.suggestions.add_more_msg') },
        ],
    };

    const chips = SUGGESTION_CHIPS[toolUsed] || [];

    const handleChipClick = (c) => {
        if (c.action === 'download_video' && toolResult?.video_url) {
            const url = toolResult.video_url.startsWith('http') ? toolResult.video_url : window.location.origin + (toolResult.video_url.startsWith('/') ? '' : '/') + toolResult.video_url;
            const a = document.createElement('a');
            a.href = url;
            a.download = `admind-video-${Date.now()}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
        }
        if (c.action === 'schedule' && onSchedule) {
            onSchedule(toolUsed, toolResult);
            return;
        }
        if (c.message) onSend(c.message);
    };

    return (
        <Box sx={{ mt: 1.5, border: '1.5px solid rgba(112,18,128,0.22)', borderRadius: '12px', overflow: 'hidden', background: 'linear-gradient(135deg, #F8F6FF 0%, #fff 100%)' }}>
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(112,18,128,0.07)', borderBottom: '1px solid rgba(112,18,128,0.1)' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#701280', animation: 'dotpulse 1.5s ease-in-out infinite', '@keyframes dotpulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } } }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#701280', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    {t('agent.agent_question')}
                </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
                <Box sx={{ fontSize: '0.88rem', color: '#1C1C28', lineHeight: 1.7, mb: chips.length > 0 ? 1.5 : 0 }}>
                    <MarkdownText text={content} />
                </Box>
                {chips.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                        {chips.map((c, i) => (
                            c.href
                                ? <Chip key={i} label={c.label} size="small" component="a" href={c.href} clickable sx={{ fontSize: '0.75rem', height: 28, background: '#F5F4FF', color: '#701280', border: '1px solid rgba(112,18,128,0.2)', fontWeight: 500, '&:hover': { background: 'rgba(112,18,128,0.12)' } }} />
                                : <Chip key={i} label={c.label} size="small" onClick={() => handleChipClick(c)} sx={{ fontSize: '0.75rem', height: 28, cursor: 'pointer', background: '#F5F4FF', color: '#701280', border: '1px solid rgba(112,18,128,0.2)', fontWeight: 500, '&:hover': { background: 'rgba(112,18,128,0.12)' } }} />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ─── Messages ─────────────────────────────────────────────────────────────────

const AgentMessage = ({ msg, onSend, onSchedule, onVideoBlocked }) => {
    const { t } = useTranslation();
    const handleRetry = onSend ? () => onSend(t('agent.retry_msg')) : null;
    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', maxWidth: '88%' }}>
            <Box sx={{ mt: 0.3 }}><AgentBlob size={40} /></Box>
            <Box sx={{ flex: 1 }}>
                {msg.loading ? (
                    <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', py: 1.2, px: 2, background: '#fff', borderRadius: '0 14px 14px 14px', border: '1px solid rgba(0,0,0,0.07)', width: 'fit-content' }}>
                        {[0, 150, 300].map(d => (
                            <Box key={d} sx={{ width: 7, height: 7, borderRadius: '50%', background: '#701280', animation: 'dotpulse 1.2s ease-in-out infinite', animationDelay: `${d}ms`, '@keyframes dotpulse': { '0%,100%': { opacity: 0.2 }, '50%': { opacity: 1 } } }} />
                        ))}
                        {msg._toolActivity && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#701280', ml: 0.5, fontStyle: 'italic' }}>
                                {msg._toolActivity}...
                            </Typography>
                        )}
                    </Box>
                ) : msg.tool_used && msg.tool_result ? (
                    <>
                        <ToolResultCard
                            tool_used={msg.tool_used}
                            tool_result={msg.tool_result}
                            onRetry={handleRetry}
                            onVideoBlocked={onVideoBlocked}
                        />
                        {msg.content && <AgentQuestion content={msg.content} toolUsed={msg.tool_used} toolResult={msg.tool_result} onSend={onSend} onSchedule={onSchedule} />}
                        {msg.tool_used !== 'save_to_calendar' && msg.tool_used !== 'generate_video' && <ActionBar toolUsed={msg.tool_used} toolResult={msg.tool_result} />}
                    </>
                ) : (
                    <Box sx={{ background: '#fff', borderRadius: '0 14px 14px 14px', p: 2, border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                        <Box sx={{ fontSize: '0.9rem', color: '#1C1C28', lineHeight: 1.7 }}>
                            <MarkdownText text={msg.content} />
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const UserMessage = ({ msg }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        {msg.imagePreview && (
            <Box sx={{ maxWidth: '75%' }}>
                <Box component="img" src={msg.imagePreview} alt="uploaded" sx={{ maxWidth: '100%', maxHeight: 180, borderRadius: '14px 14px 0 14px', objectFit: 'cover', border: '2px solid rgba(112,18,128,0.3)' }} />
            </Box>
        )}
        <Box sx={{ maxWidth: '75%', background: 'linear-gradient(135deg, #701280, #8E3A9E)', borderRadius: '14px 14px 0 14px', px: 2, py: 1.5, boxShadow: '0 2px 10px rgba(112,18,128,0.2)' }}>
            <Typography sx={{ fontSize: '0.9rem', color: '#fff', lineHeight: 1.65 }}>{msg.content}</Typography>
        </Box>
    </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const SIDEBAR_W = 240;

function MarketingAgent() {
    const { user } = useContext(AuthContext);
    const { t, i18n } = useTranslation();

    const INITIAL_MESSAGE = {
        role: 'assistant',
        content: t('agent.initial_msg'),
        tool_used: null,
        tool_result: null,
        isInitial: true,
    };

    const QUICK_ACTIONS = [
        { label: t('agent.quick.linkedin_label'), prompt: t('agent.quick.linkedin_prompt') },
        { label: t('agent.quick.article_label'), prompt: t('agent.quick.article_prompt') },
        { label: t('agent.quick.campaign_label'), prompt: t('agent.quick.campaign_prompt') },
        { label: t('agent.quick.visual_label'), prompt: t('agent.quick.visual_prompt') },
        { label: t('agent.quick.video_label'), prompt: t('agent.quick.video_prompt') },
        { label: t('agent.quick.editorial_label'), prompt: t('agent.quick.editorial_prompt') },
    ];

    const [messages, setMessages] = useState([INITIAL_MESSAGE]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [savedSessions, setSavedSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [chipSchedule, setChipSchedule] = useState(null);
    const [videoBlockedModal, setVideoBlockedModal] = useState(null);
    const [showFreeLimitModal, setShowFreeLimitModal] = useState(false);
    const [videoCreditsModalOpen, setVideoCreditsModalOpen] = useState(false);
    const [subData, setSubData] = useState(null);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    const hasUserMessage = messages.some(m => m.role === 'user');

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        setMessages(prev => {
            if (prev.length === 0 || !prev[0].isInitial) return prev;
            return [{ ...prev[0], content: t('agent.initial_msg') }, ...prev.slice(1)];
        });
    }, [i18n.language, t]);

    const loadSessions = useCallback(async () => {
        setSessionsLoading(true);
        try {
            const resp = await api.get('/api/openai/chat-sessions/');
            setSavedSessions(resp.data.filter(s => s.system === 'agent'));
        } catch (e) {}
        setSessionsLoading(false);
    }, []);

    useEffect(() => { loadSessions(); }, [loadSessions]);

    useEffect(() => {
        api.get('/billing/stripe/subscription/').then(r => setSubData(r.data)).catch(() => {});
    }, []);

    const _needsVideoCredits = () => {
        if (!subData) return false;
        const plan = subData.plan || '';
        const balance = parseFloat(subData.video_credit_balance_euros || '0');
        const secondsUsed = subData.video_seconds_used || 0;
        if (plan === 'pro_max') return secondsUsed >= 30 && balance < 5;
        return balance < 5;
    };

    const handleVideoQuickAction = (prompt) => {
        if (_needsVideoCredits()) {
            setVideoCreditsModalOpen(true);
        } else {
            sendMessage(prompt);
        }
    };

    const handleNewConversation = () => {
        setMessages([INITIAL_MESSAGE]);
        setInput('');
        setCurrentSessionId(null);
        setUploadedImage(null);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const handleLoadSession = async (session) => {
        try {
            const resp = await api.get(`/api/openai/chat-sessions/${session.id}/`);
            const restored = resp.data.messages.map(m => ({
                role: m.role,
                content: m.content,
                tool_used: m.tool_used || null,
                tool_result: m.tool_result ? JSON.parse(m.tool_result) : null,
                imagePreview: m.image_preview || null,
            }));
            setMessages([INITIAL_MESSAGE, ...restored]);
            setCurrentSessionId(session.id);
            setUploadedImage(null);
        } catch (e) {}
    };

    const handleDeleteSession = async (e, sessionId) => {
        e.stopPropagation();
        try {
            await api.delete(`/api/openai/chat-sessions/${sessionId}/`);
            if (currentSessionId === sessionId) {
                setMessages([INITIAL_MESSAGE]);
                setCurrentSessionId(null);
            }
            await loadSessions();
        } catch (e) {}
    };

    const autoSave = useCallback(async (msgs, sessionId) => {
        const userMsgs = msgs.filter(m => m.role === 'user');
        if (userMsgs.length === 0) return sessionId;
        const name = (userMsgs[0].content || '').slice(0, 50) || 'Conversazione agente';
        const cleanMsgs = msgs
            .filter(m => !m.loading && !m.isInitial && (m.role === 'user' || m.role === 'assistant') && (m.content || m.tool_result || m.imagePreview))
            .map(m => ({
                role: m.role,
                content: m.content || '',
                tool_used: m.tool_used || null,
                tool_result: m.tool_result || null,
                image_preview: m.imagePreview || null,
            }));
        try {
            if (sessionId) {
                await api.patch(`/api/openai/chat-sessions/${sessionId}/`, { messages: cleanMsgs });
                return sessionId;
            } else {
                const resp = await api.post('/api/openai/chat-sessions/', { name, messages: cleanMsgs, system: 'agent' });
                return resp.data.id;
            }
        } catch (e) {
            return sessionId;
        }
    }, []);

    const processImageFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const dataUrl = ev.target.result;
            const base64 = dataUrl.split(',')[1];
            setUploadedImage({ base64, mime: file.type || 'image/jpeg', preview: dataUrl, name: file.name });
        };
        reader.readAsDataURL(file);
    };

    const handleFileChange = (e) => { processImageFile(e.target.files?.[0]); e.target.value = ''; };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.types.includes('Files')) setDragOver(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
    const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); const file = e.dataTransfer.files?.[0]; if (file) processImageFile(file); };

    const sendMessage = async (text) => {
        if (!text.trim() || loading) return;
        const imagePreview = uploadedImage?.preview || null;
        const imageBase64 = uploadedImage?.base64 || '';
        const imageMime = uploadedImage?.mime || 'image/jpeg';
        const userMsg = { role: 'user', content: text, imagePreview };
        const streamId = Date.now();
        const loadingMsg = { role: 'assistant', content: '', loading: true, tool_used: null, tool_result: null, _streamId: streamId };
        setMessages(prev => [...prev, userMsg, loadingMsg]);
        setInput('');
        setUploadedImage(null);
        setLoading(true);

        const apiMessages = [...messages.filter(m => !m.loading && !m.isInitial), userMsg].map(m => ({
            role: m.role,
            content: m.content || '',
        }));

        let streamedContent = '';
        let firstTextChunk = true;
        const updatedMsgsRef = { current: null };

        try {
            const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '';
            const authHeader = api.defaults?.headers?.common?.['Authorization'] || '';
            const response = await fetch('/api/openai/agent/stream/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                    ...(authHeader ? { 'Authorization': authHeader } : {}),
                },
                credentials: 'include',
                body: JSON.stringify({
                    messages: apiMessages,
                    image_base64: imageBase64,
                    image_mime: imageMime,
                    ui_language: i18n.language || 'it',
                }),
            });

            if (!response.ok) {
                let errData = {};
                try { errData = await response.json(); } catch (_) {}
                if (errData.free_limit_exceeded) {
                    setShowFreeLimitModal(true);
                    setMessages(prev => [
                        ...prev.filter(m => m._streamId !== streamId),
                        userMsg,
                        { role: 'assistant', content: t('agent.free_limit_msg'), tool_used: null, tool_result: null }
                    ]);
                    return;
                }
                throw new Error(errData.error || t('agent.error_generic'));
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const data = JSON.parse(line.slice(6));

                        if (data.type === 'tool_start') {
                            const toolLabels = {
                                create_linkedin_post: 'Post LinkedIn',
                                write_article: 'Articolo',
                                build_campaign: 'Campagna',
                                generate_image: 'Immagine',
                                generate_video: 'Video',
                                save_to_calendar: 'Calendario',
                            };
                            const label = toolLabels[data.tool] || data.tool;
                            setMessages(prev => prev.map(m =>
                                m._streamId === streamId ? { ...m, _toolActivity: label } : m
                            ));
                        } else if (data.type === 'tool_done') {
                            setMessages(prev => prev.map(m =>
                                m._streamId === streamId ? { ...m, _toolActivity: null } : m
                            ));
                        } else if (data.type === 'text') {
                            if (firstTextChunk) {
                                firstTextChunk = false;
                                setLoading(false);
                                setMessages(prev => prev.map(m =>
                                    m._streamId === streamId ? { ...m, loading: false, _toolActivity: null } : m
                                ));
                            }
                            streamedContent += data.chunk;
                            setMessages(prev => prev.map(m =>
                                m._streamId === streamId ? { ...m, content: streamedContent } : m
                            ));
                        } else if (data.type === 'done') {
                            if (data.tool_result?.video_blocked) {
                                setVideoBlockedModal({
                                    reason: data.tool_result.reason,
                                    seconds_used: data.tool_result.seconds_used,
                                    seconds_limit: data.tool_result.seconds_limit,
                                });
                            }
                            setMessages(prev => {
                                const updated = prev.map(m =>
                                    m._streamId === streamId ? {
                                        role: 'assistant',
                                        content: data.message || streamedContent,
                                        tool_used: data.tool_used || null,
                                        tool_result: data.tool_result || null,
                                        loading: false,
                                        _streamId: undefined,
                                    } : m
                                );
                                updatedMsgsRef.current = updated;
                                return updated;
                            });
                        } else if (data.type === 'error') {
                            throw new Error(data.error || t('agent.error_generic'));
                        }
                    } catch (parseErr) {
                        if (parseErr.message && !parseErr.message.includes('JSON')) {
                            throw parseErr;
                        }
                    }
                }
            }

            if (updatedMsgsRef.current) {
                const finalMsgs = updatedMsgsRef.current;
                setCurrentSessionId(prev => {
                    autoSave(finalMsgs.filter(m => !m.loading && !m.isInitial), prev).then(newId => {
                        setCurrentSessionId(newId);
                        loadSessions();
                    });
                    return prev;
                });
            }
        } catch (e) {
            setMessages(prev => [
                ...prev.filter(m => m._streamId !== streamId),
                { role: 'assistant', content: t('agent.error_generic'), tool_used: null, tool_result: null }
            ]);
        } finally {
            setLoading(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const firstName = user?.first_name || user?.email?.split('@')[0] || 'tu';

    return (
        <>
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden', backgroundColor: '#FAFAF8' }}>

            {/* ── History Sidebar ── */}
            <Box sx={{
                width: sidebarOpen ? SIDEBAR_W : 0,
                minWidth: sidebarOpen ? SIDEBAR_W : 0,
                transition: 'all 0.25s ease',
                overflow: 'hidden',
                borderRight: '1px solid rgba(0,0,0,0.07)',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
            }}>
                <Box sx={{ px: 1.5, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <HistoryIcon sx={{ fontSize: '0.95rem', color: '#701280' }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1C1C28', flex: 1 }}>{t('agent.history.title')}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#9B9BA8' }}>{savedSessions.length}</Typography>
                </Box>

                <Box sx={{ px: 1, pt: 1 }}>
                    <Box onClick={handleNewConversation} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.9,
                        borderRadius: '10px', cursor: 'pointer', border: '1px dashed rgba(112,18,128,0.3)',
                        background: 'rgba(112,18,128,0.03)',
                        '&:hover': { background: 'rgba(112,18,128,0.08)', borderColor: '#701280' },
                        transition: 'all 0.15s',
                    }}>
                        <AddIcon sx={{ fontSize: '0.95rem', color: '#701280' }} />
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#701280' }}>{t('agent.history.new_chat')}</Typography>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 0.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    {sessionsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                            <CircularProgress size={18} sx={{ color: '#701280' }} />
                        </Box>
                    ) : savedSessions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', pt: 4, px: 1.5 }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: '1.8rem', color: 'rgba(0,0,0,0.12)', mb: 0.8 }} />
                            <Typography sx={{ fontSize: '0.73rem', color: '#9B9BA8', lineHeight: 1.5 }}>
                                {t('agent.history.empty')}
                            </Typography>
                        </Box>
                    ) : (
                        savedSessions.map(s => (
                            <Box key={s.id} onClick={() => handleLoadSession(s)} sx={{
                                p: 1.1, borderRadius: '10px', cursor: 'pointer',
                                background: currentSessionId === s.id ? 'rgba(112,18,128,0.08)' : 'transparent',
                                border: currentSessionId === s.id ? '1px solid rgba(112,18,128,0.2)' : '1px solid transparent',
                                '&:hover': { background: currentSessionId === s.id ? 'rgba(112,18,128,0.1)' : 'rgba(0,0,0,0.04)' },
                                display: 'flex', alignItems: 'flex-start', gap: 0.8,
                                '&:hover .del-btn': { opacity: 1 },
                            }}>
                                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                    <Typography sx={{ fontSize: '0.77rem', fontWeight: 600, color: '#1C1C28', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {s.name}
                                    </Typography>
                                    {s.preview && (
                                        <Typography sx={{ fontSize: '0.68rem', color: '#9B9BA8', mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {s.preview}
                                        </Typography>
                                    )}
                                </Box>
                                <IconButton className="del-btn" size="small" onClick={(e) => handleDeleteSession(e, s.id)}
                                    sx={{ color: '#9B9BA8', p: 0.3, opacity: 0, '&:hover': { color: '#EF4444', opacity: 1 }, flexShrink: 0, transition: 'opacity 0.15s' }}>
                                    <DeleteOutlineIcon sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>

            {/* ── Main area ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minWidth: 0 }}>
                {/* Header */}
                <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fff', flexShrink: 0 }}>
                    <Tooltip title={sidebarOpen ? t('agent.header.hide_history') : t('agent.header.show_history')}>
                        <IconButton size="small" onClick={() => setSidebarOpen(v => !v)}
                            sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '9px', p: 0.6, '&:hover': { color: '#701280', borderColor: 'rgba(112,18,128,0.3)', background: 'rgba(112,18,128,0.06)' } }}>
                            {sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: '1rem' }} /> : <ChevronRightIcon sx={{ fontSize: '1rem' }} />}
                        </IconButton>
                    </Tooltip>
                    <AgentBlob size={36} />
                    <Box>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#1C1C28', lineHeight: 1.2 }}>Marketing Agent</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 0.6 }, '50%': { opacity: 1 } } }} />
                            <Typography sx={{ fontSize: '0.68rem', color: '#10B981', fontWeight: 600 }}>{t('agent.header.active')}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                </Box>

                {/* Messages */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 4 }, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 4 } }}>
                    {!hasUserMessage && (
                        <Box sx={{ textAlign: 'center', pt: { xs: 2, md: 4 }, pb: 1 }}>
                            <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.7rem' }, fontWeight: 800, color: '#1C1C28', mb: 0.5, letterSpacing: '-0.5px' }}>
                                {t('agent.welcome.greeting', { name: firstName })}
                            </Typography>
                            <Typography sx={{ fontSize: '0.95rem', color: '#6B6B7B', mb: 4 }}>{t('agent.welcome.subtitle')}</Typography>
                        </Box>
                    )}

                    {messages.map((msg, i) => (
                        <Box key={i}>
                            {msg.role === 'user'
                                ? <UserMessage msg={msg} />
                                : <AgentMessage
                                    msg={msg}
                                    onSend={sendMessage}
                                    onSchedule={(tu, tr) => setChipSchedule({ toolUsed: tu, toolResult: tr })}
                                    onVideoBlocked={(reason) => {
                                        setVideoCreditsModalOpen(true);
                                    }}
                                />
                            }
                        </Box>
                    ))}
                    <div ref={bottomRef} />
                </Box>

                {/* Quick actions */}
                {!hasUserMessage && (
                    <Box sx={{ px: { xs: 2, md: 4 }, pb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                        {QUICK_ACTIONS.map((a, i) => (
                            <Chip key={i} label={a.label} onClick={() => a.label === t('agent.quick.video_label') ? handleVideoQuickAction(a.prompt) : sendMessage(a.prompt)}
                                sx={{ fontSize: '0.8rem', height: 36, px: 0.5, background: '#fff', border: '1px solid rgba(112,18,128,0.2)', color: '#1C1C28', fontWeight: 500, cursor: 'pointer', '&:hover': { background: 'rgba(112,18,128,0.06)', borderColor: '#701280', color: '#701280' }, transition: 'all 0.15s' }} />
                        ))}
                    </Box>
                )}

                {/* Input */}
                <Box
                    sx={{ px: { xs: 2, md: 4 }, pb: { xs: 2, md: 3 }, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: '#fff', flexShrink: 0 }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {uploadedImage && (
                        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, background: 'rgba(112,18,128,0.05)', borderRadius: '12px', border: '1px solid rgba(112,18,128,0.15)' }}>
                            <Box component="img" src={uploadedImage.preview} alt="anteprima" sx={{ width: 48, height: 48, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#701280' }}>{t('agent.input.attached')}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9B9BA8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uploadedImage.name}</Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setUploadedImage(null)} sx={{ color: '#9B9BA8', '&:hover': { color: '#FF6B35' } }}><CloseIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
                        </Box>
                    )}
                    {dragOver && (
                        <Box sx={{ mb: 1.5, p: 2, borderRadius: '12px', border: '2px dashed #701280', background: 'rgba(112,18,128,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <AttachFileIcon sx={{ fontSize: '1.1rem', color: '#701280' }} />
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#701280' }}>{t('agent.input.drop')}</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', background: dragOver ? 'rgba(112,18,128,0.04)' : '#FAFAF8', borderRadius: '16px', border: dragOver ? '1.5px solid #701280' : '1.5px solid rgba(112,18,128,0.15)', px: 2, py: 1, '&:focus-within': { borderColor: '#701280', boxShadow: '0 0 0 3px rgba(112,18,128,0.08)' }, transition: 'all 0.2s' }}>
                        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
                        <Tooltip title={t('agent.input.attach_tooltip')}>
                            <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={loading}
                                sx={{ color: uploadedImage ? '#701280' : '#9B9BA8', flexShrink: 0, '&:hover': { color: '#701280' }, mb: 0.3 }}>
                                <AttachFileIcon sx={{ fontSize: '1.15rem' }} />
                            </IconButton>
                        </Tooltip>
                        <TextField
                            inputRef={inputRef}
                            fullWidth multiline maxRows={5}
                            placeholder={t('agent.input.placeholder')}
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                            disabled={loading}
                            variant="standard"
                            InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', color: '#1C1C28', lineHeight: 1.6, py: 0.5 } }}
                            sx={{ '& .MuiInputBase-input::placeholder': { color: '#9B9BA8', fontSize: '0.88rem' } }}
                        />
                        <IconButton onClick={() => sendMessage(input)} disabled={!input.trim() || loading}
                            sx={{ width: 38, height: 38, background: input.trim() && !loading ? 'linear-gradient(135deg, #701280, #8E3A9E)' : 'rgba(0,0,0,0.06)', color: '#fff', borderRadius: '11px', flexShrink: 0, transition: 'all 0.2s', '&:hover': { transform: 'scale(1.05)' }, '&:disabled': { background: 'rgba(0,0,0,0.06)', color: '#bbb' } }}>
                            {loading ? <CircularProgress size={16} sx={{ color: '#701280' }} /> : <SendIcon sx={{ fontSize: '1rem' }} />}
                        </IconButton>
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', color: '#9B9BA8', mt: 1, textAlign: 'center' }}>
                        {t('agent.input.footer')}
                    </Typography>
                </Box>
            </Box>
        </Box>

        {chipSchedule && (
            <ScheduleDialog
                open={true}
                onClose={() => setChipSchedule(null)}
                toolUsed={chipSchedule.toolUsed}
                toolResult={chipSchedule.toolResult}
            />
        )}

        {showFreeLimitModal && (
            <PricingModal open={true} onClose={() => setShowFreeLimitModal(false)} currentPlan={null} />
        )}

        {/* Video credits modal — shown for all video-blocked cases */}
        <VideoCreditsModal
            open={videoCreditsModalOpen || !!(videoBlockedModal?.reason)}
            onClose={(credited) => {
                setVideoCreditsModalOpen(false);
                setVideoBlockedModal(null);
                if (credited) {
                    api.get('/billing/stripe/subscription/').then(r => setSubData(r.data)).catch(() => {});
                }
            }}
            reason={videoBlockedModal?.reason || 'need_video_credits'}
        />
        </>
    );
}

export default MarketingAgent;
